import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Box } from "@mui/material";

const SequentialScrollView = ({ components, durations, height = '100dvh' }) => {
  const sectionRef = useRef(null);
  const [step, setStep] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Start the animation when the section is in view
            setStep(0); // Reset to the first step
          } else {
            // Reset the animation when the section is out of view
            setStep(-1); // Reset to no step
          }
        });
      },
      { threshold: 0.3 } // Trigger when 30% of the section is visible
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (step >= 0 && step < components.length - 1) {
      // Move to the next step after the specified duration
      const timer = setTimeout(() => setStep(step + 1), durations[step]);
      return () => clearTimeout(timer);
    }
  }, [step, components, durations]);

  return (
    <Box
      ref={sectionRef}
      sx={{
        zIndex: 1,
        position: "relative",
        height: height,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        textAlign: "center",
        scrollSnapAlign: 'start',
      }}
    >
      <AnimatePresence mode="wait">
        {step >= 0 && step < components.length && (
          <motion.div
            key={`step-${step}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 1 }}
          >
            {components[step]}
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default SequentialScrollView;